// Drawing a map, once.
//
// Both web apps draw the same road index on an SVG, and before this
// module they each did it their own way. Two readings of one projection
// is how this package's own header says a screen ends up claiming
// something that is not so, and it did: both apps drew a degree of
// longitude at a degree of latitude's scale, so both stretched Québec
// sideways by 1.43x. The console then computed vehicle bearings *with*
// the cosine, leaving the chevron pointing up to ten degrees off the road
// it sat on; the consumer app papered over the same fault with a
// hard-coded 1.9 longitude ratio, tuned by eye to a squashed picture.
//
// So the arithmetic lives here and the pictures stay separate. What is
// shared is what is true of any map: how a place becomes a point, how a
// point becomes a place again, what a gesture means, how much a wheel
// should turn, and how much detail is worth drawing at a given width.
// What is *not* shared is either app's opinion — the console draws a
// fleet across a region, the consumer app draws one leg to one door, and
// forcing those into one component would be a worse map twice.
//
// Framework-free, like the rest of this package: no React, no DOM beyond
// a rectangle's numbers.

/** A box of ground. */
export interface MapExtent {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

/** A pointer's position, in client coordinates. */
export interface MapPoint { x: number; y: number }

/** As much of a DOMRect as any of this needs. */
export interface MapBox { left: number; top: number; width: number; height: number }

/** How far a view may be zoomed, as a span of latitude in degrees. */
export interface SpanLimits { min: number; max: number }

/** One degree of latitude, near enough, anywhere. */
export const KM_PER_DEGREE = 111;

/**
 * How many leaves of the road index a basemap should read.
 *
 * `roadsIn` keeps `hits.slice(0, maxLeaves)` — the first intersecting
 * leaves *in index order*, not the nearest — and a leaf is a cell of
 * ground, so exceeding the cap does not thin a map out evenly. It blanks
 * whole rectangles, wherever they fall in the index. The engine's default
 * of 24 is sized for `coverage.ts`, which asks about a single point and
 * passes 1; a basemap covering a town needs far more, and a map with
 * rectangular holes in it is worse than a slow one.
 */
export const BASEMAP_LEAVES = 256;

/** Painting order, least important first, so a motorway lands on top. */
export const ROAD_LAYERS = [
  "residential", "tertiary", "secondary", "primary", "trunk", "motorway"
] as const;

export type RoadLayer = (typeof ROAD_LAYERS)[number];

/** The drawable class of a road, from whatever the index called it. */
export function classOf(roadClass: string): RoadLayer {
  const value = String(roadClass || "").toLowerCase();
  if (value.startsWith("motorway")) return "motorway";
  if (value.startsWith("trunk")) return "trunk";
  if (value.startsWith("primary")) return "primary";
  if (value.startsWith("secondary")) return "secondary";
  if (value.startsWith("tertiary")) return "tertiary";
  return "residential";
}

/*
  How much of the network is worth drawing at a given width of view.

  Every extract is mostly residential — everything unclassified lands
  there too — so a wide view drawn in full is a grey mat: half a million
  metres of cul-de-sac rendered so small that the arterials carrying the
  actual round disappear into it. Zoomed out, a reader is following
  *shape*, and the detail is not merely wasted, it is what destroys the
  reading.

  Detail comes off later than feels natural on paper, because the common
  case for both apps is one town and the uncommon one is a province.

  This is a drawing decision and says nothing about coverage: nothing here
  is missing from the index, and `roadsIn`'s own `truncated` still reports
  the different case where the engine could not return everything.
*/
const DETAIL: { upToKm: number; keep: number }[] = [
  { upToKm: 25, keep: 6 },      // a town or three: everything
  { upToKm: 60, keep: 5 },      // a county: no residential
  { upToKm: 150, keep: 4 },     // a region: no tertiary either
  { upToKm: 400, keep: 3 },     // a province: primary and above
  { upToKm: Infinity, keep: 2 } // further out: the skeleton only
];

/** The classes to paint for a view this wide. */
export function detailFor(widthKm: number): Set<RoadLayer> {
  const keep = DETAIL.find(step => widthKm <= step.upToKm)!.keep;
  return new Set(ROAD_LAYERS.slice(ROAD_LAYERS.length - keep));
}

/** How much shorter a degree of longitude is at this latitude. */
export function lonSquashAt(latitude: number): number {
  return Math.cos((latitude * Math.PI) / 180);
}

/**
 * The span of longitude that fills a canvas of this shape.
 *
 * The alternative — a constant ratio — is only right at one latitude, and
 * the consumer app's hard-coded 1.9 was right at none of them: it was
 * chosen to look correct on a picture that was already stretched.
 */
export function lonSpanFor(spanLat: number, midLat: number, aspect: number): number {
  return (spanLat * aspect) / lonSquashAt(midLat);
}

/**
 * Fit an extent into a drawing box.
 *
 * A degree of longitude is shorter than a degree of latitude everywhere
 * but the equator, by the cosine of the latitude. Applying it here is
 * what puts the cities back in proportion and makes a true bearing land
 * on the road it belongs to by construction.
 */
export function projector(
  extent: MapExtent,
  width: number,
  height: number,
  padding = 0
): {
  project(lat: number, lon: number): [number, number];
  /** The inverse, so a point somebody touched becomes a place. */
  unproject(x: number, y: number): [number, number];
  /** How wide the view is on the ground, for the detail rules. */
  widthKm: number;
} {
  const spanLat = Math.max(extent.maxLat - extent.minLat, 1e-9);
  const spanLon = Math.max(extent.maxLon - extent.minLon, 1e-9);
  const midLat = (extent.minLat + extent.maxLat) / 2;
  const squash = lonSquashAt(midLat);
  const spanX = spanLon * squash;
  const scale = Math.min((width - padding * 2) / spanX, (height - padding * 2) / spanLat);
  const offsetX = (width - spanX * scale) / 2;
  const offsetY = (height - spanLat * scale) / 2;
  return {
    project: (lat, lon) => [
      offsetX + (lon - extent.minLon) * squash * scale,
      offsetY + (extent.maxLat - lat) * scale
    ],
    unproject: (x, y) => [
      extent.maxLat - (y - offsetY) / scale,
      extent.minLon + (x - offsetX) / (squash * scale)
    ],
    widthKm: spanX * KM_PER_DEGREE
  };
}

/**
 * Where a point on the screen actually is, in degrees.
 *
 * A canvas drawn with `preserveAspectRatio="…slice"` is scaled to *cover*
 * its element and centred, with the overflow cropped, so getting from a
 * client coordinate back to a place means undoing that cover-scale as
 * well as the projection. A gesture that skips it drifts away from the
 * fingers doing it, worse the further the element's shape is from the
 * viewBox's. Pass `cover: false` for a canvas that is letterboxed to fit
 * instead.
 */
export function geoAtClient(
  clientX: number,
  clientY: number,
  rect: MapBox,
  width: number,
  height: number,
  unproject: (x: number, y: number) => [number, number],
  cover = true
): [number, number] {
  const fit = cover
    ? Math.max(rect.width / width, rect.height / height)
    : Math.min(rect.width / width, rect.height / height);
  const x = (clientX - rect.left - (rect.width - width * fit) / 2) / fit;
  const y = (clientY - rect.top - (rect.height - height * fit) / 2) / fit;
  return unproject(x, y);
}

/** Scale an extent about a fixed place, which stays under the finger. */
export function zoomAbout(
  base: MapExtent,
  factor: number,
  anchor: [number, number],
  limits: SpanLimits
): MapExtent {
  const spanLat = base.maxLat - base.minLat;
  // Clamping the factor rather than the result keeps latitude and
  // longitude in step; scaling them independently would shear the map.
  const wanted = Math.min(Math.max(spanLat * factor, limits.min), limits.max);
  const applied = wanted / spanLat;
  const [lat, lon] = anchor;
  return {
    minLat: lat + (base.minLat - lat) * applied,
    maxLat: lat + (base.maxLat - lat) * applied,
    minLon: lon + (base.minLon - lon) * applied,
    maxLon: lon + (base.maxLon - lon) * applied
  };
}

/** Slide an extent so `from` ends up where `to` is. */
export function panBy(
  base: MapExtent,
  from: [number, number],
  to: [number, number]
): MapExtent {
  const dLat = from[0] - to[0];
  const dLon = from[1] - to[1];
  return {
    minLat: base.minLat + dLat, maxLat: base.maxLat + dLat,
    minLon: base.minLon + dLon, maxLon: base.maxLon + dLon
  };
}

/** The midpoint of the fingers on the glass. */
export function middle(points: MapPoint[]): MapPoint {
  const x = points.reduce((total, point) => total + point.x, 0) / points.length;
  const y = points.reduce((total, point) => total + point.y, 0) / points.length;
  return { x, y };
}

/** How far apart the two outermost fingers are. */
export function spread(points: MapPoint[]): number {
  if (points.length < 2) return 0;
  return Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y);
}

/**
 * One gesture model for one finger and for two.
 *
 * Take where the touched places were before the move and where they are
 * after; scale the extent about the first midpoint by how much the
 * fingers spread, then slide it so that place lands under the new
 * midpoint. With one finger the spread is always 1 and it reduces to a
 * pan — which is why a drag tracks the ground under it exactly rather
 * than approximately.
 *
 * The invariant, asserted in `test/map.test.ts`: whatever the fingers do,
 * the place under their midpoint when the move began is under their
 * midpoint when it ends.
 */
export function gestureExtent(
  base: MapExtent,
  before: MapPoint[],
  after: MapPoint[],
  rect: MapBox,
  options: { width: number; height: number; limits: SpanLimits; cover?: boolean }
): MapExtent {
  if (!before.length || before.length !== after.length) return base;
  if (!rect.width || !rect.height) return base;
  const { width, height, limits, cover = true } = options;

  const at = (point: MapPoint, extent: MapExtent): [number, number] =>
    geoAtClient(
      point.x, point.y, rect, width, height,
      projector(extent, width, height).unproject, cover
    );

  const midBefore = middle(before);
  const midAfter = middle(after);

  let factor = 1;
  if (before.length > 1 && after.length > 1) {
    const spreadBefore = spread(before);
    const spreadAfter = spread(after);
    // Fingers apart means a smaller span: the ground between them has to
    // grow to keep up with them.
    if (spreadBefore > 0 && spreadAfter > 0) factor = spreadBefore / spreadAfter;
  }

  // The place under the fingers when the move started. It is the one
  // thing the gesture promises not to lose.
  const anchor = at(midBefore, base);
  const zoomed = zoomAbout(base, factor, anchor, limits);
  // Where that place ended up once the span changed, then slid to sit
  // under the fingers' new middle.
  return panBy(zoomed, anchor, at(midAfter, zoomed));
}

/**
 * How much one wheel event should zoom.
 *
 * A flat factor per event — which both apps used — is roughly right for a
 * mouse, whose wheel arrives in discrete notches of about a hundred, and
 * catastrophic for a trackpad, which streams dozens of events carrying a
 * few pixels each: half a second of two-finger scroll on a MacBook
 * compounded to about two hundred and thirty times.
 *
 * So the step follows the distance actually reported. `exp()` keeps it
 * multiplicative, so the same scroll zooms by the same *proportion*
 * wherever it starts, and reversing the gesture undoes it exactly.
 *
 * macOS reports a trackpad pinch as a wheel event with `ctrlKey` set and
 * a small delta, which wants more zoom per pixel than a scroll does —
 * fingers on glass expect to move the map further than a swipe.
 */
export function zoomStep(event: {
  deltaY: number;
  deltaMode?: number;
  ctrlKey?: boolean;
}): number {
  // deltaMode 1 is lines and 2 is pages; both are rare, and both are
  // meaningless unless converted to something comparable to pixels.
  const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1;
  const pixels = event.deltaY * scale;
  const perPixel = event.ctrlKey ? 0.012 : 0.002;
  const factor = Math.exp(pixels * perPixel);
  // One violent flick should not cross the whole zoom range in a frame.
  return Math.min(Math.max(factor, 1 / 1.5), 1.5);
}

/**
 * How far apart two vertices must land before both are worth drawing, in
 * drawing units.
 *
 * Road geometry is surveyed in metres and does not care what it is being
 * drawn at. Zoomed out, a cul-de-sac arrives with forty vertices that
 * project into the same two pixels, and the browser is asked to hold
 * every one: a complete basemap of three towns produced six megabytes of
 * path data before this existed.
 */
const MIN_STEP = 0.9;

/**
 * A road as an SVG path, with vertices that would land on top of each
 * other left out. The first and last points are always kept, because
 * shortening a road *would* change the picture. Returns "" for a road
 * that projects to nothing.
 */
export function polyline(
  points: [number, number][],
  project: (lat: number, lon: number) => [number, number],
  minStep = MIN_STEP
): string {
  if (points.length < 2) return "";
  const out: string[] = [];
  let lastX = 0;
  let lastY = 0;
  for (let i = 0; i < points.length; i++) {
    const [x, y] = project(points[i]![0], points[i]![1]);
    const isEnd = i === 0 || i === points.length - 1;
    if (!isEnd && Math.abs(x - lastX) < minStep && Math.abs(y - lastY) < minStep) continue;
    out.push(`${out.length === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`);
    lastX = x;
    lastY = y;
  }
  return out.length > 1 ? out.join(" ") : "";
}
